import {
  CheckCircleOutline,
  Close,
  ContactPage,
  Description,
  Edit,
  Event,
  OpenInNew,
  TaskAlt,
  Timeline,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";

import { Route } from "~services/router/Route";
import { AddContactModal } from "~ui/molecules/AddContactModal/AddContactModal";
import { AddInteractionModal } from "~ui/molecules/AddInteractionModal/AddInteractionModal";
import { AddNoteModal } from "~ui/molecules/AddNoteModal/AddNoteModal";
import { AddTaskModal } from "~ui/molecules/AddTaskModal/AddTaskModal";
import { DealsTab } from "~ui/molecules/ProspectTabs/Deals/DealsTab";
import { TaskModal } from "~ui/molecules/TaskModal/TaskModal";

import { ProspectAdditionalDetails } from "../../molecules/ProspectAdditionalDetails/ProspectAdditionalDetails";
import { ProspectInfo } from "../../molecules/ProspectInfo/ProspectInfo";
import { type CarouselTaskItem } from "../TaskDashboardTemplate/taskUtils";
import { useProspectDetailsSlideout, type UseProspectDetailsSlideoutOptions } from "./useProspectDetailsSlideout";

const tTitle = "Prospect Details";
const tViewFullDetails = "View full details";
const tClose = "Close";
const tAddContact = "Add Contact";
const tAddNote = "Add Note";
const tAddInteraction = "Add Interaction";
const tAddTask = "Add Task";
const tScheduleMeeting = "Schedule Meeting";
const tTaskFromCalendar = "Task";
const tCompleteTask = "Complete";
const tEditTask = "Edit Task";

interface ProspectDetailsSlideoutProps {
  /** When opening from the task calendar, shows this task and optional completion. */
  contextTask?: CarouselTaskItem | null;
  isCompletingContextTask?: boolean;
  onClose: () => void;
  onCompleteContextTask?: (task: CarouselTaskItem) => void | Promise<void>;
  onSalesTaskUpdated?: UseProspectDetailsSlideoutOptions["onSalesTaskUpdated"];
  prospectId: string;
}

export const ProspectDetailsSlideout = ({
  contextTask,
  isCompletingContextTask = false,
  onClose,
  onCompleteContextTask,
  onSalesTaskUpdated,
  prospectId,
}: ProspectDetailsSlideoutProps) => {
  const {
    addProspectToDeal,
    deals,
    canChangeOwner,
    handleContactCardClick,
    handleOwnerChange,
    handleScheduleMeeting,
    isAddingProspectToDeal,
    isLoadingDeals,
    isUpdatingOwner,
    modals,
    ownerOptions,
    primaryContact,
    primaryEmail,
    primaryPhone,
    prospect,
    noteModal,
    interactionModal,
    taskModal,
  } = useProspectDetailsSlideout(prospectId, { onSalesTaskUpdated });

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(prospectId)}
        onClose={onClose}
        slotProps={{
          paper: {
            "aria-label": tTitle,
            sx: { width: 400 },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header */}
          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", p: 2 }}>
            <Typography variant="h6">{tTitle}</Typography>
            <Box display="flex" gap={1}>
              <IconButton
                onClick={() => {
                  onClose();
                  window.open(Route.salesPortalProspectDetails({ prospectId }), "_blank");
                }}
                size="small"
                color="primary"
                title={tViewFullDetails}
              >
                <OpenInNew />
              </IconButton>
              <IconButton aria-label={tClose} onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
          <Divider />

          {contextTask ? (
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, py: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {tTaskFromCalendar}
              </Typography>
              <Typography fontWeight={600} gutterBottom variant="body1">
                {contextTask.title}
              </Typography>
              {contextTask.dueAt ? (
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Due {DateTime.fromISO(contextTask.dueAt, { setZone: true }).toLocal().toFormat("ccc, MMM d • h:mm a")}
                </Typography>
              ) : null}
              {contextTask.description ? (
                <Typography
                  sx={{
                    mb:
                      (onCompleteContextTask && contextTask.id) || (contextTask.type === "sales" && contextTask.id)
                        ? 1.5
                        : 0,
                    whiteSpace: "pre-wrap",
                  }}
                  variant="body2"
                >
                  {contextTask.description}
                </Typography>
              ) : null}
              {(onCompleteContextTask && contextTask.id) || (contextTask.type === "sales" && contextTask.id) ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {contextTask.type === "sales" && contextTask.id ? (
                    <Button
                      onClick={() => taskModal.openEditModal(contextTask)}
                      size="small"
                      startIcon={<Edit />}
                      variant="outlined"
                    >
                      {tEditTask}
                    </Button>
                  ) : null}
                  {onCompleteContextTask && contextTask.id ? (
                    <Button
                      color="success"
                      disabled={isCompletingContextTask}
                      onClick={() => void Promise.resolve(onCompleteContextTask(contextTask))}
                      size="small"
                      startIcon={
                        isCompletingContextTask ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : (
                          <CheckCircleOutline />
                        )
                      }
                      variant="outlined"
                    >
                      {isCompletingContextTask ? "Completing…" : tCompleteTask}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          ) : null}

          {/* Quick Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-evenly", p: 2 }}>
            <Tooltip title={tAddInteraction}>
              <IconButton
                size="small"
                onClick={() => modals.openInteractionModal()}
                disabled={!modals.openInteractionModal}
                color="primary"
                aria-label={tAddInteraction}
              >
                <Timeline fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tAddContact}>
              <IconButton
                size="small"
                onClick={() => modals.openAddModal()}
                disabled={!modals.openAddModal}
                color="primary"
                aria-label={tAddContact}
              >
                <ContactPage fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tAddNote}>
              <IconButton
                size="small"
                onClick={() => modals.openNoteModal()}
                disabled={!modals.openNoteModal}
                color="primary"
                aria-label={tAddNote}
              >
                <Description fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tAddTask}>
              <IconButton
                size="small"
                onClick={() => modals.openTaskModal()}
                disabled={!modals.openTaskModal}
                color="primary"
                aria-label={tAddTask}
              >
                <TaskAlt fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={tScheduleMeeting}>
              <IconButton size="small" onClick={handleScheduleMeeting} color="primary" aria-label={tScheduleMeeting}>
                <Event fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {prospect && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, overflow: "auto", p: 2 }}>
              <ProspectInfo
                prospect={prospect}
                primaryEmail={primaryEmail}
                primaryPhone={primaryPhone}
                primaryContact={primaryContact}
                onContactClick={handleContactCardClick}
                isUpdatingOwner={isUpdatingOwner}
                onOwnerChange={canChangeOwner ? handleOwnerChange : undefined}
                ownerOptions={ownerOptions}
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Additional Details
                </Typography>
                <ProspectAdditionalDetails
                  region={prospect.region ?? undefined}
                  source={prospect.source ?? undefined}
                  estimatedValue={prospect.estimatedValue ?? undefined}
                  expectedCloseDate={prospect.expectedCloseDate ?? undefined}
                  timeZone={prospect.timeZone ?? undefined}
                />
              </Box>

              <DealsTab
                compact
                deals={deals}
                isAddingProspectToDeal={isAddingProspectToDeal}
                isLoadingDeals={isLoadingDeals}
                onAddToDeal={addProspectToDeal}
                prospect={prospect}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Contact Modals */}
      {modals.isAddOpen && modals.handleAddContact && (
        <AddContactModal open={modals.isAddOpen} onClose={modals.closeAddModal} onSubmit={modals.handleAddContact} />
      )}
      {modals.editContact && modals.handleUpdateContact && (
        <AddContactModal
          open={true}
          onClose={modals.closeEditModal}
          onSubmit={modals.handleUpdateContact}
          initialValues={{
            email: modals.editContact?.email ?? undefined,
            firstName: modals.editContact?.firstName ?? "",
            isPrimary: modals.editContact?.isPrimary ?? false,
            lastName: modals.editContact?.lastName ?? "",
            phone: modals.editContact?.phone ?? undefined,
            roleTitle: modals.editContact?.roleTitle ?? undefined,
          }}
          isEdit
        />
      )}

      {/* Other Modals */}
      {noteModal.isAddOpen && noteModal.handleAddNote && (
        <AddNoteModal open={noteModal.isAddOpen} onClose={noteModal.closeAddModal} onSubmit={noteModal.handleAddNote} />
      )}
      {interactionModal.isAddOpen && interactionModal.handleAddInteraction && (
        <AddInteractionModal
          open={interactionModal.isAddOpen}
          onClose={interactionModal.closeAddModal}
          onSubmit={interactionModal.handleAddInteraction}
        />
      )}
      {taskModal.isAddOpen && taskModal.handleCreateTask && (
        <AddTaskModal
          open={taskModal.isAddOpen}
          onClose={taskModal.closeAddModal}
          onSubmit={taskModal.handleCreateTask}
        />
      )}
      {taskModal.editTask ? (
        <TaskModal
          open
          onClose={taskModal.closeEditModal}
          onSubmit={taskModal.handleUpdateTask}
          task={taskModal.editTask}
          title={tEditTask}
        />
      ) : null}
    </>
  );
};
